import mixpanel from "mixpanel-browser";
import { cioanalytics } from "./customerio";
import { hasSession } from "../auth/tokenStorage";

const DEBUG = process.env.REACT_APP_ANALYTICS_DEBUG === "true";
const ANON_KEY = "vaultsa_anonymous_id";
const USER_ID_KEY = "vaultsa_user_id";

export function getAnonymousId() {
  let id = localStorage.getItem(ANON_KEY);
  if (!id) {
    id =
      "anon_" +
      Date.now().toString(36) +
      "_" +
      Math.random().toString(36).slice(2, 10);
    localStorage.setItem(ANON_KEY, id);
  }
  return id;
}

function debug(...args) {
  if (DEBUG) console.debug("[analytics]", ...args);
}

function baseContext() {
  const userId = localStorage.getItem(USER_ID_KEY);
  return {
    anonymous_id: getAnonymousId(),
    user_id: userId || undefined,
    is_authenticated: !!userId || hasSession(),
  };
}

export function track(event, props = {}) {
  const payload = { ...baseContext(), ...props };
  debug("track", event, payload);
  try {
    mixpanel.track(event, payload);
  } catch (e) {
    debug("mixpanel track error", e);
  }
  try {
    cioanalytics.track(event, payload);
  } catch (e) {
    debug("cio track error", e);
  }
}

function coreUserTraits(user) {
  if (!user) return {};
  return {
    user_id: user.id?.toString?.() || user.user_id,
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    created_at: user.created_at || user.date_joined,
    last_login_at: user.last_login_at || new Date().toISOString(),
    signup_method: user.signup_method || "email",
    is_authenticated: true,
    role: user.role,
    is_seller: user.is_seller || user.role === "seller",
    seller_status: user.seller_status || "none",
    seller_approved_at: user.seller_approved_at || null,
    seller_rejected_at: user.seller_rejected_at || null,
  };
}

export function identifyUser(user) {
  const id = user?.id?.toString?.() || user?.user_id;
  if (!id) return;
  try {
    localStorage.setItem(USER_ID_KEY, id);
  } catch {}
  const traits = coreUserTraits(user);
  debug("identify", id, traits);
  try {
    mixpanel.identify(id);
    mixpanel.people.set({
      $email: traits.email,
      $first_name: traits.first_name,
      $last_name: traits.last_name,
      ...traits,
    });
  } catch (e) {
    debug("mixpanel identify error", e);
  }
  try {
    cioanalytics.identify(id, traits);
  } catch (e) {
    debug("cio identify error", e);
  }
}

export function aliasOnSignup(user) {
  const id = user?.id?.toString?.() || user?.user_id;
  if (!id) return;
  try {
    localStorage.setItem(USER_ID_KEY, id);
  } catch {}
  const anon = getAnonymousId();
  debug("alias", anon, "->", id);
  try {
    mixpanel.alias(id, anon);
  } catch (e) {
    debug("mixpanel alias error", e);
  }
  try {
    cioanalytics.alias(id, anon);
  } catch (e) {
    debug("cio alias error", e);
  }
  identifyUser(user);
}

export function trackFirstTime(milestoneKey, eventName, props = {}) {
  const storageKey = `vaultsa_milestone_${milestoneKey}`;
  try {
    if (localStorage.getItem(storageKey)) return;
  } catch {
    return;
  }
  track(eventName, props);
  try {
    localStorage.setItem(storageKey, "1");
  } catch {}
}

export function resetAnalytics() {
  try {
    mixpanel.reset();
  } catch {}
  try {
    cioanalytics.reset();
  } catch {}
  localStorage.removeItem(ANON_KEY);
  localStorage.removeItem(USER_ID_KEY);
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("vaultsa_milestone_")) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {}
}
